import classnames from 'classnames';
import { getConnectionOrVendorType } from 'js/lib/ResourceUtils';
import { getEmptyRequiredFields, formatValidationErrorMessage } from 'js/lib/ValidationUtils';
import {
  clone,
  cloneDeep,
  concat,
  filter,
  get,
  has,
  isEmpty,
  isNil,
  isPlainObject,
  map,
  mapKeys,
  omit,
  pick,
} from 'lodash';
import React from 'react';
import { Redirect } from 'react-router-dom';
import Form from '../elements/forms/Form';
import FormPage from '../elements/forms/FormPage';
import CircularLoader from '../elements/progress/CircularLoader';
import { getInitialTemplateId, getTemplateFilteredFields } from './CredentialForm.helpers';

import UIUtils from '../../lib/UIUtils';
import {
  oauthServerFlow,
  filterOAuthFormValues,
  buildOAuthTemplateConfig,
} from 'new/services/oauthServerFlow';
import {
  hasOAuthTriggerField,
  isServerOAuthButton,
  isServerOAuthConnector,
  resolveTemplateAuthType,
  userSuppliesOAuthApp,
} from 'new/utils/serverOAuthEligibility';
import OAuth1GenericRESTService from '../../services/OAuth1GenericRESTService';
import OAuth1aService from '../../services/OAuth1aService';
import OAuth2GenericRESTService from '../../services/OAuth2GenericRESTService';
import OAuth2Service from '../../services/OAuth2Service';
import SharePointService from 'js/services/SharePointService';
import BoxService from '../../services/BoxService';
import DropboxService from '../../services/DropboxService';
import GCSService from '../../services/GCSService';
import GDriveService from '../../services/GDriveService';
import ZendeskService from '../../services/ZendeskService';
import BigQueryService from '../../services/BigQueryService';
import IntuitService from '../../services/IntuitService';
import PubSubService from '../../services/PubSubService';
import JSONUtils from '../../lib/JSONUtils';
import { INTUIT_QB_VENDOR_NAME } from 'js/constants/config';

import {
  CONNECTION_CATEGORY_TEMPLATIZED_API,
  CONNECTION_CATEGORY_TEMPLATIZED_API_MULTI,
} from 'js/constants/connectionCategories';
import { FORM_TYPE_EDIT_CREDENTIAL } from 'js/constants/formTypes';
import { ROUTE_CREDENTIALS } from 'js/constants/pathnames';
import { SUPPORT_KEY_CREDENTIAL } from 'js/constants/supportPageKeys';
import {
  BOX_REST_TEMPLATE,
  GBQ_TEMPLATE,
  GCS_TEMPLATE,
  GDRIVE_TEMPLATE,
  INTUIT_QUICKBOOKS_REST_TEMPLATE,
  OAUTH1_GENERIC_REST_TEMPLATE,
  OAUTH1a_REST_TEMPLATE,
  OAUTH2_GENERIC_REST_TEMPLATE,
  OAUTH2_REST_TEMPLATE,
  PUBSUB_TEMPLATE,
  SHAREPOINT_TEMPLATE,
  ZENDESK_REST_TEMPLATE,
} from 'js/constants/templateTypes';
import { NexlaAccordion } from 'new/components/NexlaAccordion';
import { FieldBuilderRedesigned } from 'new/elements/FieldBuilderRedesigned/FieldBuilder';
import { Button } from 'new/elements/support/Button';
import RoutePrefixContext from '../../../new/contexts/RoutePrefixContext';

import { withSnackbar } from 'js/utils/withSnackbar';

import styles from './CredentialForm.module.scss';
import { Alert } from '@mui/material';
import { getFeatureFlagValue } from 'new/constants/featureFlags';
import { API_SERVER_URL } from 'js/constants/api';

const mainFieldsKeys = ['cred_name', 'cred_desc', 'cred_type'];

class CredentialForm extends React.Component {
  static contextType = RoutePrefixContext;

  constructor(props) {
    super(props);

    this.state = {
      selectedTemplate:
        props.selectedTemplate ||
        getInitialTemplateId({
          credential: props.credential,
          credentialData: props.credentialData,
          connAuthById: props.paging.connAuthById,
        }),
      fields: [],
      basic: [],
      advanced: [],
      redirectPath: null,
      awaitingCredential: false,
      oauthAuthorized: false,
    };

    this._isMounted = false;
    this._renderField = this._renderField.bind(this);
    this._getFieldsData = this._getFieldsData.bind(this);
    this._initializeEditForm = this._initializeEditForm.bind(this);
    this._handleFieldUpdated = this._handleFieldUpdated.bind(this);
    this._handleButtonFieldClicked = this._handleButtonFieldClicked.bind(this);
    this._handleFormDismissed = this._handleFormDismissed.bind(this);
    this._updateCredentialData = this._updateCredentialData.bind(this);
    this._validateCredential = this._validateCredential.bind(this);
    this._authenticateVendor = this._authenticateVendor.bind(this);
    this._getForm = this._getForm.bind(this);
  }

  setupMount = async () => {
    const { credentialId } = this.props;
    if (credentialId) {
      const credResponse = await this.props.getDataCredential(credentialId);
      const credData = credResponse?.entities?.credentials?.[credResponse.result];
      const connector = credData.credentials_type;
      await this.props.loadFullVendor(connector);
      this._initializeEditForm();
    }
  };

  componentDidMount() {
    this._isMounted = true;
    this.setupMount();
    this.props.setContextualSupport(SUPPORT_KEY_CREDENTIAL);
  }

  componentWillUnmount() {
    this._isMounted = false;
    this._updateCredentialData({}, true);
    this.props.unsetContextualSupport(SUPPORT_KEY_CREDENTIAL);
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      (this.props.credential &&
        prevProps.credential !== this.props.credential &&
        this.props.vendorsLoaded) ||
      (this.props.credential && this.props.vendorsLoaded && !prevProps.vendorsLoaded)
    ) {
      if (this.props.credential?.auth_template) {
        this.setState({
          selectedTemplate: this.props.credential.auth_template.id,
        });
      }
      this._initializeEditForm(this.props.credential);
    }

    if (
      (prevProps.credentialId !== this.props.credentialId ||
        prevProps.credentialData !== this.props.credentialData ||
        prevProps.vendorsLoaded !== this.props.vendorsLoaded ||
        prevProps.paging !== this.props.paging) &&
      !this.props.loadingVendorInfo
    ) {
      const fields = this._getFieldsData(this.props.credentialData);
      this.setState(fields);
    }

    if (
      this.props.loadingVendorInfo !== prevProps.loadingVendorInfo &&
      !this.props.loadingVendorInfo
    ) {
      const fieldsData = this._getFieldsData(this.props.credentialData);
      const selectedTemplate = getInitialTemplateId({
        credential: this.props.credential,
        credentialData: this.props.credentialData,
        connAuthById: this.props.paging.connAuthById,
      });
      this.setState({
        selectedTemplate,
        ...fieldsData,
      });
    }

    if (this.state.selectedTemplate !== prevState.selectedTemplate) {
      this.setState(this._getFieldsData(this.props.credentialData), () => {
        const newCredentialData = UIUtils.configWithMissingDefaults(
          this.props.credentialData,
          this.state.fields,
        );

        if (!isEmpty(newCredentialData)) {
          this._updateCredentialData(newCredentialData);
        }
      });
    }

    // when credentialData or fields are newly loaded, fill data with defaults
    if (
      !isEmpty(this.props.credentialData) &&
      !isEmpty(this.state.fields) &&
      this.state.fields.length > 2 && // fields should contain more than just name and desc
      (isEmpty(prevProps.credentialData) ||
        isEmpty(prevState.fields) ||
        prevState.fields.length <= 2)
    ) {
      const newCredentialData = UIUtils.configWithMissingDefaults(
        this.props.credentialData,
        this.state.fields,
      );

      if (!isEmpty(newCredentialData)) {
        this._updateCredentialData(newCredentialData);
      }
      const {
        credentialData,
        paging: { connAuthById },
      } = this.props;
      if (credentialData?.cred_type) {
        const { cred_type } = credentialData;
        const effectiveConnectionType = get(credentialData, '_vendorConnectionType') || cred_type;
        const { templates } = clone(connAuthById[effectiveConnectionType]);
        return templates[0]?.id;
      }
    }
  }

  render() {
    if (this.state.redirectPath) {
      return <Redirect to={this.state.redirectPath} />;
    }

    const {
      isModal,
      isPopUpForm,
      credentialId,
      credentialData: { name },
      renderFooter,
      onCancel,
      isLoading,
      className,
    } = this.props;
    const { fields, basic, advanced, awaitingCredential } = this.state;
    const fieldsMap = mapKeys(fields, 'name');
    const basicFields = map(
      basic.filter((name) => !!fieldsMap[name]),
      (name) => fieldsMap[name],
    );
    const advancedFields = map(
      advanced.filter((name) => !!fieldsMap[name]),
      (name) => fieldsMap[name],
    );

    return (
      <>
        {isPopUpForm ? (
          <>
            <div className={styles.credential_inputs_container}>
              {this._getForm(basicFields, advancedFields)}
            </div>

            {typeof renderFooter === 'function' ? (
              renderFooter(this.onClickSubmit, onCancel)
            ) : (
              <div className={styles.footer}>
                <Button color="info" variant="outlined" onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  variant="primary"
                  disabled={awaitingCredential || isLoading}
                  onClick={this.onClickSubmit}
                >
                  Save
                </Button>
              </div>
            )}
          </>
        ) : (
          <Form
            isModal={isModal}
            className={className}
            title={credentialId ? 'Edit Credentials' : 'Add Credentials'}
            type={FORM_TYPE_EDIT_CREDENTIAL}
            subtitle={name}
            onSubmit={this._handleFormDismissed}
            onDismiss={this._handleFormDismissed}
            alwaysShowCancel={true}
            submitButtonText="Save"
          >
            <FormPage
              title=""
              validationMethod={this._validateCredential}
              nextButtonText="Save"
              contentHeight={800}
            >
              {this._getForm(basicFields, advancedFields)}
            </FormPage>
          </Form>
        )}
      </>
    );
  }

  onClickSubmit = () => {
    this._validateCredential({}, this.props.onSubmit, this.props.onError);
  };

  _getForm(basicFields, advancedFields) {
    const {
      isPopUpForm,
      loadingVendorInfo = false,
      isLoading,
      credentialData,
      paging: { metaInfoById },
    } = this.props;
    const { awaitingCredential } = this.state;

    const loading = isLoading || awaitingCredential || loadingVendorInfo;

    // Show the redirect instruction only when admin-api is the one being redirected to. The
    // trigger must be an OAuth2 control: an oauth1a form still dances in the browser and comes
    // back to the UI's own route, so admin-api's URL would be the wrong thing to register.
    const oauth2Trigger = basicFields.some((f) => f.name === 'oauth2.fetch_token_button');
    const showRedirectUrl =
      getFeatureFlagValue('UI_OAUTH_SERVER_FLOW') &&
      oauth2Trigger &&
      userSuppliesOAuthApp(basicFields) &&
      this._usesServerOAuth('oauth2.fetch_token_button', undefined, credentialData);

    return (
      <form autoComplete="new-password" className={classnames({ 'credential-form': !isPopUpForm })}>
        <section
          className={classnames([
            { 'configuration-holder': !isPopUpForm },
            styles.fields_container,
          ])}
        >
          {showRedirectUrl && (
            <Alert severity="info" sx={{ marginBottom: 'var(--spacing-sm)' }}>
              Redirect URL: <strong>{API_SERVER_URL}/oauth/callback</strong> — add this to your
              OAuth app&apos;s allowed redirect URLs.
            </Alert>
          )}
          {map(basicFields, this._renderField)}

          {!isEmpty(advancedFields) && (
            <div
              className={classnames([
                'advanced-settings-container',
                isPopUpForm
                  ? styles.advanced_fields_container_pop_up
                  : styles.advanced_fields_container_simple_form,
              ])}
            >
              <NexlaAccordion
                title="Advanced Settings"
                contentClassname={styles.advanced_fields_content}
                children={map(advancedFields, this._renderField)}
              />
            </div>
          )}

          {loading ? (
            <div className="vendors-loading-container">
              <CircularLoader size={40} thickness={2} />
            </div>
          ) : null}
        </section>
      </form>
    );
  }

  _renderField(field, key) {
    const { credentialData } = this.props;

    if (
      UIUtils.shouldHideField(field, credentialData) === true ||
      !UIUtils.shouldRenderField(field, credentialData)
    ) {
      return null;
    }

    const defaultFieldValue =
      field.type === 'nonce' ? UIUtils.generateNonce() : field.default || null;
    const currentFieldValue = has(credentialData, field.name)
      ? credentialData[field.name]
      : defaultFieldValue;
    const isRequired = field.required && !isPlainObject(field.required) && !has(field, 'default');
    const showRequiredIndicator = field.showRequiredIndicator !== false;
    const id = field.id || 'form-text-' + field.name;

    return (
      <FieldBuilderRedesigned
        key={key}
        field={field}
        currentFieldValue={currentFieldValue}
        isRequired={isRequired}
        showRequiredIndicator={showRequiredIndicator}
        id={id}
        fieldLabelClassName={styles.input_label}
        prefix={this.context}
        onChange={this._handleFieldUpdated}
        inModal={this.props.inModal}
      />
    );
  }

  onChangeRestApiType = (connector) => {
    this.onChangeCredentialType(connector);
  };

  _getFieldsData(credentialData) {
    const {
      credentialId,
      vendors,
      paging: { metaInfoById, connAuthById, connAuthMeta, credentialsAllowed },
    } = this.props;
    const { cred_type } = credentialData;
    let authFields = [];
    let templates = [];
    let items = [];
    let authFieldsMeta = { basic: [], advanced: [] };

    if (cred_type) {
      const effectiveConnectionType = get(credentialData, '_vendorConnectionType') || cred_type;

      ({ templates, items } = connAuthById[effectiveConnectionType]);
      authFields = clone(getTemplateFilteredFields(templates, items, this.state.selectedTemplate));
      authFieldsMeta = cloneDeep(connAuthMeta[effectiveConnectionType]);
      // get vendors matching this cred_type and list them in API Type field
      if (!credentialId) {
        const vendorOptions = map(
          filter(
            metaInfoById,
            ({ connectionType, connectionCategory }) =>
              connectionType === cred_type &&
              [
                CONNECTION_CATEGORY_TEMPLATIZED_API,
                CONNECTION_CATEGORY_TEMPLATIZED_API_MULTI,
              ].includes(connectionCategory),
          ),
          (meta) => ({ label: meta.displayName, value: meta.key }),
        );

        if (!isEmpty(vendorOptions)) {
          const connectionName = metaInfoById[cred_type].displayName;

          authFields.push({
            name: '_vendorConnectionType',
            display_name: 'API Type',
            type: 'string',
            inputType: 'select',
            required: true,
            default: cred_type,
            onChange: this.onChangeRestApiType,
            values: [
              {
                label: `Generic ${connectionName}`,
                value: cred_type,
              },
              ...vendorOptions,
            ],
          });
          authFieldsMeta.basic.unshift('_vendorConnectionType');
        }
      }

      // add oauth2 fields
      const vendorMeta =
        metaInfoById[(credentialData && credentialData._vendorConnectionType) || cred_type];

      const vendor = vendors && vendors[vendorMeta && vendorMeta.vendorId];

      const authType = (() => {
        const vendorAuthType = get(vendor, 'config.authType');
        if (!vendorAuthType || typeof vendorAuthType === 'string') return vendorAuthType;
        const template =
          templates?.find((t) => t.id === this.state.selectedTemplate) || templates?.[0];
        return vendorAuthType[template?.name] || vendorAuthType;
      })();

      // Suppress the generic OAuth button when the connector provides its own
      // (identified by inputType === 'button' with a value ending in '.fetch_token').
      const hasCustomAuthButton = authFields.some(
        (f) =>
          f.inputType === 'button' &&
          typeof f.value === 'string' &&
          f.value.endsWith('.fetch_token'),
      );

      if (authType === 'oauth2' && !hasCustomAuthButton) {
        authFields.push(
          {
            name: 'oauth2.fetch_token_button',
            display_name: 'Authorize',
            description: 'Fetch Authentication Token',
            type: 'button',
            required: true,
            order: 30,
            conditionalDisplay: true,
            displayOn: [
              {
                name: 'oauth2.access_token',
                value: '',
              },
            ],
            key: 'oauth2.fetch_token_button',
            inputType: 'button',
          },
          {
            name: 'oauth2.access_token',
            display_name: 'Access Token',
            description: 'OAuth 2 Access Token',
            type: 'string',
            required: true,
            order: null,
            isHiddenField: true,
            key: 'oauth2.access_token',
            inputType: 'text',
          },
        );
        authFieldsMeta.basic.push('oauth2.access_token', 'oauth2.fetch_token_button');
      }
      if (authType === 'oauth1a' && !hasCustomAuthButton) {
        authFields.push(
          {
            name: 'oauth1a.fetch_token_button',
            display_name: 'Authorize',
            description: 'Fetch Authentication Token',
            type: 'button',
            required: true,
            order: 30,
            conditionalDisplay: true,
            displayOn: [
              {
                name: 'oauth1a.access_token',
                value: '',
              },
            ],
            key: 'oauth1a.fetch_token_button',
            inputType: 'button',
          },
          {
            name: 'oauth1a.access_token',
            display_name: 'Access Token',
            description: 'OAuth 1 Access Token',
            type: 'string',
            required: true,
            order: null,
            isHiddenField: true,
            key: 'oauth1a.access_token',
            inputType: 'text',
          },
        );
        authFieldsMeta.basic.push('oauth1a.access_token', 'oauth1a.fetch_token_button');
      }
    }

    let fields = [
      {
        id: 'cred_name',
        name: 'cred_name',
        display_name: 'Credential Name',
        required: true,
      },
      {
        id: 'cred_desc',
        name: 'cred_desc',
        display_name: 'Credential Description',
        required: false,
        style: { marginBottom: 30 },
      },
    ];
    if (templates?.length > 1) {
      fields.push({
        id: 0,
        display_name: 'Authentication Type',
        inputType: 'select',
        name: 'template',
        type: 'string',
        required: true,
        // templates are already in display order; don't re-sort by label
        unsorted: true,
        default: `${this.state.selectedTemplate || templates[0].id}`,
        options: templates.map((template) => ({
          label: template.display_name || 'Auth Template ' + template.id,
          value: `${template.id}`,
        })),
        onChange: this.onChangeTemplate,
      });
      authFieldsMeta.basic.unshift('template');
    }
    fields = authFields ? concat(fields, authFields) : fields;
    authFieldsMeta.basic = ['cred_name', 'cred_desc', ...authFieldsMeta.basic];
    if (!credentialId) {
      fields.unshift({
        id: 'cred_type',
        name: 'cred_type',
        display_name: 'Credential Type',
        required: true,
        onChange: this.onChangeCredentialType,
        inputType: 'select',
        values: map(credentialsAllowed, (id) => ({
          value: id,
          label: metaInfoById[id].displayName,
        })),
        preserveValues: true,
      });
      authFieldsMeta.basic.unshift('cred_type');
    }

    // field styles
    fields = map(fields, (field) => {
      switch (field.type) {
        case 'boolean':
          return {
            ...field,
            labelStyle: {
              color: '#879fb4',
              fontWeight: 500,
              letterSpacing: 1,
              textTransform: 'uppercase',
              fontSize: 11,
            },
            iconStyle: {
              fill: '#4ad496',
            },
          };

        case 'button':
          return {
            ...field,
            style: {
              marginBottom: 20,
            },
          };

        default:
      }

      return field;
    });

    return { fields, ...authFieldsMeta };
  }

  _initializeEditForm() {
    const { credential, vendors } = this.props;
    const connectionOrVendorType = getConnectionOrVendorType(
      credential,
      vendors,
      'credentials_type',
      'vendor_id',
    );

    const newData = {
      cred_name: credential?.name,
      cred_desc: credential?.description || '',
      cred_type: connectionOrVendorType,
      ...credential?.credentials_non_secure_data,
    };

    this._updateCredentialData(newData);
  }

  _handleFieldUpdated(name, value, isButtonField = false) {
    const {
      credentialData,
      paging: { connAuthById },
    } = this.props;
    let newData;

    if (
      (name === 'cred_type' && value !== credentialData.cred_type) ||
      (name === '_vendorConnectionType' && value !== credentialData._vendorConnectionType)
    ) {
      newData = pick(credentialData, mainFieldsKeys);
    } else {
      newData = { ...credentialData };
    }

    newData[name] = value;

    // omit fields which shouldn't be rendered (but aren't "hidden" (hidden == true))
    const effectiveConnectionType = newData._vendorConnectionType || newData.cred_type;
    const { templates, items } = connAuthById[effectiveConnectionType];
    const template = (() => {
      if (this.state.selectedTemplate) {
        const selected = templates?.find((t) => t.id === this.state.selectedTemplate);
        if (selected) {
          return selected;
        }
      }
      return templates?.[0];
    })();
    // Intentionally not using getTemplateFilteredFields here: this guard skips
    // filtering entirely at exactly one template, unlike the other call sites.
    const connectionFields =
      !templates || !templates.length || templates.length === 1
        ? items
        : items.filter((field) => template.auth_parameters.includes(field.param_id));
    const invisibleFieldNames = map(
      filter(connectionFields, (field) => !UIUtils.shouldRenderField(field, newData)),
      'name',
    );
    const visibleFieldData = omit(newData, invisibleFieldNames);
    const finalFieldData = UIUtils.configWithMissingDefaults(visibleFieldData, connectionFields);

    if (isButtonField) {
      this._handleButtonFieldClicked(name, value, finalFieldData);
    } else {
      this._updateCredentialData(finalFieldData, true);
    }
  }

  /** Connector + control both eligible for the server-side dance. */
  _usesServerOAuth(name, value, data) {
    if (!isServerOAuthButton(name, value)) return false;

    const {
      paging: { metaInfoById },
      credentialData,
      vendors,
    } = this.props;
    const connectionType =
      data?._vendorConnectionType ||
      data?.cred_type ||
      credentialData?._vendorConnectionType ||
      credentialData?.cred_type;
    const meta = metaInfoById?.[connectionType];
    const vendor = vendors?.[meta?.vendorId];
    const templates = this.props.paging?.connAuthById?.[connectionType]?.templates ?? [];
    // Fall back to the only template when none is explicitly selected, so an object-keyed
    // `authType` still resolves on a single-template vendor. Matches NewCredentialsForm.
    const selected =
      templates.find((t) => t.id === Number(this.state.selectedTemplate)) ?? templates[0];

    return isServerOAuthConnector({
      connectionCategory: meta?.connectionCategory,
      hasOAuthTrigger: hasOAuthTriggerField(this.state.fields),
      authType: resolveTemplateAuthType(vendor?.config?.authType, selected?.name),
    });
  }

  _handleButtonFieldClicked(name, value, data) {
    // Hand off to admin-api only for flows it can actually run. Everything else — OAuth1/1a,
    // generic REST, unmigrated connectors — falls through to the legacy dispatch below, which
    // is byte-identical to its behaviour with the flag off.
    if (getFeatureFlagValue('UI_OAUTH_SERVER_FLOW') && this._usesServerOAuth(name, value, data)) {
      this._authenticateVendorServerFlow(data);
      return;
    }
    if (value === 'rest.template_type' && has(data, 'rest.template_type')) {
      this._authenticateVendor(data['rest.template_type']);
    } else if (name === 'oauth2.fetch_token_button') {
      this._authenticateVendor(OAUTH2_REST_TEMPLATE, data);
    } else if (name === 'oauth2.generic_rest.fetch_token_button') {
      this._authenticateVendor(OAUTH2_GENERIC_REST_TEMPLATE, data);
    } else if (name === 'oauth1a.fetch_token_button') {
      this._authenticateVendor(OAUTH1a_REST_TEMPLATE, data);
    } else if (name === 'oauth1.generic_rest.fetch_token_button') {
      this._authenticateVendor(OAUTH1_GENERIC_REST_TEMPLATE, data);
    } else if (name === 'zendesk.fetch_token_button') {
      this._authenticateVendor(ZENDESK_REST_TEMPLATE, data);
    } else if (name === 'box.fetch_token_button') {
      this._authenticateVendor(BOX_REST_TEMPLATE, data);
    } else if (name === 'gbq.fetch_token_button') {
      this._authenticateVendor(GBQ_TEMPLATE, data);
    } else if (name === 'gcs.fetch_token_button') {
      this._authenticateVendor(GCS_TEMPLATE, data);
    } else if (name === 'gdrive.fetch_token_button') {
      this._authenticateVendor(GDRIVE_TEMPLATE, data);
    } else if (name === 'sharepoint.fetch_token_button') {
      this._authenticateVendor(SHAREPOINT_TEMPLATE, data);
    } else if (name === 'google_pubsub.fetch_token_button') {
      this._authenticateVendor(PUBSUB_TEMPLATE, data);
    } else if (name === 'intuit_qb.fetch_token_button') {
      this._authenticateVendor(INTUIT_QUICKBOOKS_REST_TEMPLATE, data);
    } else {
      switch (value) {
        case 'dropbox.fetch_token':
          DropboxService.authenticate((authInfo) => {
            if (authInfo && has(authInfo, 'access_token')) {
              this._updateCredentialData(DropboxService.createInfoFromAuthResult(authInfo));
              this._onOAuthSuccess();
            }
          }, data);
          break;

        case 'box.fetch_token':
          BoxService.authenticate((authInfo) => {
            if (authInfo && has(authInfo, 'access_token')) {
              this._updateCredentialData(BoxService.createInfoFromAuthResult(authInfo));
              this._onOAuthSuccess();
            }
          }, data);
          break;

        default:
          break;
      }
    }
  }

  onChangeCredentialType = (type) => {
    this.setState(
      {
        selectedTemplate: null,
        oauthAuthorized: false,
      },
      () => {
        this.props.loadFullVendor(type)?.then(() => {
          if (this.props.credential && this.props.vendorsLoaded) {
            this._initializeEditForm();
          }
        });
      },
    );
  };

  _handleFormDismissed() {
    if (this.props.isModal) {
      this.props.onDismiss?.();
      return;
    }
    this.setState({ redirectPath: `${this.context}${ROUTE_CREDENTIALS}` });
  }

  _onOAuthSuccess = () => {
    this.setState({ oauthAuthorized: true });
  };

  _updateCredentialData(data, overwrite = false) {
    const { credentialData, onChange } = this.props;
    const newData = overwrite ? data : { ...credentialData, ...data };

    const id = newData?.template;
    const info = { ...newData };
    if (!isNil(id) && id != this.state.selectedTemplate) {
      const effectiveConnectionType =
        get(credentialData, '_vendorConnectionType') || newData.cred_type;

      const { templates, items } = this.props.paging?.connAuthById?.[effectiveConnectionType] || {};
      const authFields = getTemplateFilteredFields(templates, items, this.state.selectedTemplate);
      authFields.forEach((field) => {
        delete info?.[field.name];
      });
    }

    if (typeof onChange === 'function') {
      onChange(info);
    }

    this.props.updateFormData(FORM_TYPE_EDIT_CREDENTIAL, {
      credentialData: info,
    });
  }

  onChangeTemplate = (id) => {
    if (id) {
      this.setState({ selectedTemplate: Number(id) });
    }
    if (this.props.onChangeTemplate) {
      this.props.onChangeTemplate(Number(id));
    }
  };

  _validateCredential(data, onSuccess, onError) {
    const { credentialId, credential, credentialData } = this.props;

    const { fields, awaitingCredential } = this.state;

    if (awaitingCredential) return;

    const effectiveConnectionType =
      credentialData._vendorConnectionType || credentialData.cred_type;
    const emptyRequiredFields = getEmptyRequiredFields(fields, credentialData);

    const errorMessage = formatValidationErrorMessage(emptyRequiredFields, {
      credentialNameError: !credentialData.cred_name
        ? 'Please enter a name for this credential'
        : null,
    });

    if (errorMessage) {
      onError?.(errorMessage);
      return;
    }

    //TEMP: check validations and build fields with defaults.
    let selectedCredentialInfo = { ...credentialData };

    if (this.state.selectedTemplate) {
      selectedCredentialInfo.auth_template_id = this.state.selectedTemplate;
    }

    // omit fields which shouldn't be rendered
    const invisibleFieldNames = map(
      filter(fields, (field) => !UIUtils.shouldRenderField(field, selectedCredentialInfo)),
      'name',
    );
    const visibleCredentialData = omit(selectedCredentialInfo, invisibleFieldNames);

    if (!isEmpty(visibleCredentialData)) {
      selectedCredentialInfo = visibleCredentialData;
    }

    const finalCredentialInfo = UIUtils.configWithMissingDefaults(selectedCredentialInfo, fields);

    this.setState({ awaitingCredential: true }, () => {
      const probePromise =
        credentialId && credential
          ? this.props.updateCredentialAndProbe(
              credentialId,
              finalCredentialInfo,
              FORM_TYPE_EDIT_CREDENTIAL,
            )
          : this.props.addCredentialAndProbe(effectiveConnectionType, finalCredentialInfo);

      probePromise
        ?.then(() => {
          if (!this._isMounted) return;
          this.setState({ awaitingCredential: false });
          const credentialName = credentialData?.cred_name || 'Credential';
          this.props.showSuccess?.(`${credentialName} saved successfully`);
          onSuccess();
        })
        .catch((err) => {
          if (!this._isMounted) return;
          this.setState({ awaitingCredential: false });
          if (err?.isSaveError) {
            onError(err.message || 'Unable to save credential.');
          } else {
            onError(err?.message || 'Connection test failed.');
          }
        });
    });
  }

  /**
   * Server-side dance. Only reached when `_handleButtonFieldClicked` has confirmed the
   * connector and the clicked control are both something admin-api can run.
   */
  _authenticateVendorServerFlow(data) {
    const {
      paging: { metaInfoById },
      credentialData,
    } = this.props;

    const effectiveConnectionType =
      data._vendorConnectionType ||
      data.cred_type ||
      credentialData._vendorConnectionType ||
      credentialData.cred_type;
    const vendorId = metaInfoById?.[effectiveConnectionType]?.vendorId;
    const authTemplateId = this.state.selectedTemplate;
    const credentialName = data.cred_name || credentialData.cred_name;

    const formValues = filterOAuthFormValues(data);

    oauthServerFlow({
      nexlaSessionToken: this.props.nexlaSessionToken,
      vendorId,
      authTemplateId,
      credentialName,
      formValues: Object.keys(formValues).length > 0 ? formValues : undefined,
      // Mode B: the API returns tokens, the form is populated, the user clicks Save.
    })
      .then((result) => {
        if (result.type !== 'tokens') return;
        // Namespace the tokens the way the credential model expects (`vendor.access_token`,
        // `oauth2.access_token`, …) before they enter form state — Save copies form state
        // straight into template_config. Only the token values are ours to supply; everything
        // template-derived is resolved server-side by `enrich_config`.
        this._updateCredentialData(buildOAuthTemplateConfig({}, result.tokens, this.state.fields));
      })
      .catch((err) => {
        this.props.showError(err.message || 'Authorization failed.');
      });
  }

  _authenticateVendor(vendor, data) {
    switch (vendor) {
      case OAUTH2_REST_TEMPLATE:
        OAuth2Service.authenticate((authInfo, error) => {
          const batchUpdateInfo = {
            ...data,
            ...mapKeys(authInfo, (value, key) => `oauth2.${key}`),
          };
          if (authInfo && has(authInfo, 'access_token')) {
            batchUpdateInfo['oauth2.access_token'] = authInfo.access_token;
            this._updateCredentialData(batchUpdateInfo);
          } else {
            console.log(error);
          }
        }, data);
        break;

      case OAUTH2_GENERIC_REST_TEMPLATE:
        OAuth2GenericRESTService.authenticate((authInfo, error) => {
          if (authInfo && has(authInfo, 'access_token')) {
            this._updateCredentialData({
              'oauth2.access.token': authInfo.access_token,
              'vendor.auth_scheme': 'header',
              'vendor.token_type': authInfo.token_type,
              'vendor.access_token': authInfo.access_token,
              'vendor.has_token_expiration_ts': !!authInfo.expires_in,
              'vendor.token_expires_in': authInfo.expires_in,
              'vendor.refresh_token': authInfo.refresh_token,
              'vendor.last_refreshed_at': authInfo.authorizedAt,
            });
          } else {
            console.log(error);
          }
        }, data);
        break;

      case OAUTH1a_REST_TEMPLATE:
        OAuth1aService.authenticate((authInfo, error) => {
          const batchUpdateInfo = {
            ...data,
            ...mapKeys(authInfo, (value, key) => `oauth1a.${key}`),
          };
          if (authInfo && has(authInfo, 'access_token')) {
            batchUpdateInfo['oauth1a.access_token'] = authInfo.access_token;
            this._updateCredentialData(batchUpdateInfo);
          } else {
            console.log(error);
          }
        }, data);
        break;

      case OAUTH1_GENERIC_REST_TEMPLATE:
        OAuth1GenericRESTService.authenticate((authInfo, error) => {
          if (authInfo && has(authInfo, 'access_token')) {
            this._updateCredentialData({
              'oauth1.access.token': authInfo.access_token,
              'oauth1.access.token.secret': authInfo.oauth_token_secret,
            });
          } else {
            console.log(error);
          }
        }, data);
        break;

      case ZENDESK_REST_TEMPLATE:
        ZendeskService.authenticate((authInfo, error) => {
          if (authInfo && has(authInfo, 'accessToken')) {
            //update zendesk token
            this._handleFieldUpdated('zendesk.access_token', authInfo.accessToken);
            this._onOAuthSuccess();
            //update request headers
          } else {
            console.log(error);
          }
        }, data);
        break;

      case BOX_REST_TEMPLATE:
        BoxService.authenticate((authInfo) => {
          if (authInfo && has(authInfo, 'access_token')) {
            let batchUpdateInfo = {
              'vendor.name': 'box',
              access_token: authInfo.access_token,
              'auth.type': authInfo['auth.type'],
              'oauth2.access.token.method': authInfo['oauth2.access.token.method'],
              'oauth2.access.token.url': authInfo['oauth2.access.token.url'],
              'oauth2.client.id': authInfo['oauth2.client.id'],
              'oauth2.client.secret': authInfo['oauth2.client.secret'],
              'oauth2.token.type.override': authInfo['oauth2.token.type.override'],
              'vendor.refresh_url': authInfo['vendor.refresh_url'],
              'vendor.auth_scheme': authInfo['vendor.auth_scheme'],
              'vendor.access_token': authInfo.access_token,
              'vendor.token_type': authInfo.token_type,
              'vendor.last_refreshed_at': authInfo.authorizedAt,
              'vendor.refresh_token': authInfo.refresh_token,
              'vendor.token_expires_in': authInfo.expires_in,
              'vendor.has_token_expiration_ts': true,
              'vendor.other_info': '{}',
            };
            this._updateCredentialData(batchUpdateInfo);
            this._onOAuthSuccess();
          }
        }, data);
        break;

      case INTUIT_QUICKBOOKS_REST_TEMPLATE:
        IntuitService.authenticate((authInfo) => {
          if (authInfo && has(authInfo, 'access_token')) {
            let vendorOther = {
              x_refresh_token_expires_in: authInfo.x_refresh_token_expires_in,
              realmid: authInfo.realmid,
            };
            let vendorString = JSONUtils.stringifyJson(vendorOther, '');
            let batchUpdateInfo = {
              'vendor.name': INTUIT_QB_VENDOR_NAME,
              'vendor.access_token': authInfo.access_token,
              'vendor.token_type': authInfo.token_type,
              'vendor.refresh_token': authInfo.refresh_token,
              'vendor.last_refreshed_at': authInfo.authorizedAt,
              'vendor.token_expires_in': authInfo.expires_in,
              'vendor.auth_scheme': authInfo.auth_scheme,
              'vendor.scopes': authInfo.scopes,
              'vendor.has_token_expiration_ts': true,
              'vendor.other_info': `${vendorString}`,
            };
            this._updateCredentialData(batchUpdateInfo);
            this._onOAuthSuccess();
          }
        }, data);
        break;

      case GBQ_TEMPLATE:
        BigQueryService.authenticate((authInfo, error) => {
          if (authInfo && has(authInfo, 'access_token')) {
            let batchUpdateInfo = {
              access_token: authInfo.access_token,
              'auth.type': authInfo['auth.type'],
              'oauth2.access.token.method': authInfo['oauth2.access.token.method'],
              'oauth2.access.token.url': authInfo['oauth2.access.token.url'],
              'oauth2.client.id': authInfo['oauth2.client.id'],
              'oauth2.client.secret': authInfo['oauth2.client.secret'],
              'oauth2.token.type.override': authInfo['oauth2.token.type.override'],
              'vendor.refresh_url': authInfo['vendor.refresh_url'],
              'vendor.auth_scheme': authInfo['vendor.auth_scheme'],
              'vendor.scopes': authInfo.scope,
              'vendor.name': 'bigquery',
              'vendor.access_token': authInfo.access_token,
              'vendor.token_type': authInfo.token_type,
              'vendor.last_refreshed_at': authInfo.authorizedAt,
              'vendor.refresh_token': authInfo.refresh_token,
              'vendor.token_expires_in': authInfo.expires_in,
              'vendor.has_token_expiration_ts': true,
              'vendor.other_info': '{}',
            };
            this._updateCredentialData(batchUpdateInfo);
            this._onOAuthSuccess();
          } else {
            console.log(error);
          }
        }, data);
        break;

      case GCS_TEMPLATE:
        GCSService.authenticate((authInfo, error) => {
          if (authInfo && has(authInfo, 'access_token')) {
            let batchUpdateInfo = {
              access_token: authInfo.access_token,
              'auth.type': authInfo['auth.type'],
              'oauth2.access.token.method': authInfo['oauth2.access.token.method'],
              'oauth2.access.token.url': authInfo['oauth2.access.token.url'],
              'oauth2.client.id': authInfo['oauth2.client.id'],
              'oauth2.client.secret': authInfo['oauth2.client.secret'],
              'oauth2.token.type.override': authInfo['oauth2.token.type.override'],
              'vendor.refresh_url': authInfo['vendor.refresh_url'],
              'vendor.auth_scheme': authInfo['vendor.auth_scheme'],
              'vendor.scopes': authInfo.scope,
              'vendor.name': 'gcs',
              'vendor.access_token': authInfo.access_token,
              'vendor.token_type': authInfo.token_type,
              'vendor.last_refreshed_at': authInfo.authorizedAt,
              'vendor.refresh_token': authInfo.refresh_token,
              'vendor.token_expires_in': authInfo.expires_in,
              'vendor.has_token_expiration_ts': true,
              'vendor.other_info': '{}',
            };
            this._updateCredentialData(batchUpdateInfo);
            this._onOAuthSuccess();
          } else {
            console.log(error);
          }
        }, data);
        break;

      case GDRIVE_TEMPLATE:
        GDriveService.authenticate((authInfo, error) => {
          if (authInfo && has(authInfo, 'access_token')) {
            let batchUpdateInfo = {
              access_token: authInfo.access_token,
              'auth.type': authInfo['auth.type'],
              'oauth2.access.token.method': authInfo['oauth2.access.token.method'],
              'oauth2.access.token.url': authInfo['oauth2.access.token.url'],
              'oauth2.client.id': authInfo['oauth2.client.id'],
              'oauth2.client.secret': authInfo['oauth2.client.secret'],
              'oauth2.token.type.override': authInfo['oauth2.token.type.override'],
              'vendor.refresh_url': authInfo['vendor.refresh_url'],
              'vendor.auth_scheme': authInfo['vendor.auth_scheme'],
              'vendor.scopes': authInfo.scope,
              'vendor.name': 'gdrive',
              'vendor.access_token': authInfo.access_token,
              'vendor.token_type': authInfo.token_type,
              'vendor.last_refreshed_at': authInfo.authorizedAt,
              'vendor.refresh_token': authInfo.refresh_token,
              'vendor.token_expires_in': authInfo.expires_in,
              'vendor.has_token_expiration_ts': true,
              'vendor.other_info': '{}',
            };
            this._updateCredentialData(batchUpdateInfo);
            this._onOAuthSuccess();
          } else {
            console.log(error);
          }
        }, data);
        break;

      case SHAREPOINT_TEMPLATE:
        SharePointService.authenticate((authInfo, error) => {
          if (authInfo && has(authInfo, 'access_token')) {
            let batchUpdateInfo = {
              'vendor.name': 'sharepoint',
              access_token: authInfo.access_token,
              'auth.type': authInfo['auth.type'],
              'oauth2.access.token.method': authInfo['oauth2.access.token.method'],
              'oauth2.access.token.url': authInfo['oauth2.access.token.url'],
              'oauth2.client.id': authInfo['oauth2.client.id'],
              'oauth2.client.secret': authInfo['oauth2.client.secret'],
              'oauth2.token.type.override': authInfo['oauth2.token.type.override'],
              'vendor.refresh_url': authInfo['vendor.refresh_url'],
              'vendor.auth_scheme': authInfo['vendor.auth_scheme'],
              'vendor.scopes': authInfo.scope,
              'vendor.access_token': authInfo.access_token,
              'vendor.token_type': authInfo.token_type,
              'vendor.last_refreshed_at': authInfo.authorizedAt,
              'vendor.refresh_token': authInfo.refresh_token,
              'vendor.token_expires_in': authInfo.expires_in,
              'vendor.has_token_expiration_ts': true,
              'vendor.other_info': '{}',
            };
            this._updateCredentialData(batchUpdateInfo);
            this._onOAuthSuccess();
          } else {
            console.log(error);
          }
        }, data);
        break;

      case PUBSUB_TEMPLATE:
        PubSubService.authenticate((authInfo, error) => {
          if (authInfo && has(authInfo, 'access_token')) {
            let batchUpdateInfo = {
              access_token: authInfo.access_token,
              'auth.type': authInfo['auth.type'],
              'oauth2.access.token.method': authInfo['oauth2.access.token.method'],
              'oauth2.access.token.url': authInfo['oauth2.access.token.url'],
              'oauth2.client.id': authInfo['oauth2.client.id'],
              'oauth2.client.secret': authInfo['oauth2.client.secret'],
              'oauth2.token.type.override': authInfo['oauth2.token.type.override'],
              'vendor.refresh_url': authInfo['vendor.refresh_url'],
              'vendor.auth_scheme': authInfo['vendor.auth_scheme'],
              'vendor.scopes': authInfo.scope,
              'vendor.name': 'google_pubsub',
              'vendor.access_token': authInfo.access_token,
              'vendor.token_type': authInfo.token_type,
              'vendor.last_refreshed_at': authInfo.authorizedAt,
              'vendor.refresh_token': authInfo.refresh_token,
              'vendor.token_expires_in': authInfo.expires_in,
              'vendor.has_token_expiration_ts': true,
              'vendor.other_info': '{}',
            };
            this._updateCredentialData(batchUpdateInfo);
            this._onOAuthSuccess();
          } else {
            console.log(error);
          }
        }, data);
        break;

      default:
        break;
    }
  }
}

CredentialForm.defaultProps = {
  isPopUpForm: false,
};

export default withSnackbar(CredentialForm);
